<script setup lang="ts">
import { GpBrandImage, GpModal } from '@gportal/uikit';
import { nextTick, ref, watch } from 'vue';
import ModalFooterBtn from './ModalFooterBtn.vue';

const props = withDefaults(
  defineProps<{
    show: boolean;
    question?: string;
    confirmText?: string;
    confirmIcon?: string | string[];
    loading?: boolean;
    confirmType?:
      | 'primary'
      | 'flat'
      | 'danger'
      | 'warning'
      | 'success'
      | 'info'
      | 'cancel'
      | 'transparent';
  }>(),
  {
    question: 'Are you sure about this?',
    confirmText: 'Confirm',
    confirmIcon: undefined,
    loading: false,
    confirmType: 'primary',
  },
);

const emit = defineEmits<{
  close: [value: boolean];
  confirm: [value: boolean];
}>();

const onCancel = () => {
  if (props.show) emit('close', false);
};

const onConfirm = () => {
  if (props.show) emit('confirm', true);
};

const confirmButton = ref<InstanceType<typeof ModalFooterBtn>>();
watch(
  () => props.show,
  async () => {
    if (props.show) {
      await nextTick();
      await nextTick();
      (confirmButton.value.$el as HTMLButtonElement).focus();
    }
  },
);
</script>

<template>
  <GpModal
    :is-opened="show"
    :content-text="question"
    :confirm-text="confirmText"
    :close-on-backdrop="!loading"
    :close-on-escape="!loading"
    @close="onCancel"
    @confirm="onConfirm"
  >
    <template #header>
      <GpBrandImage class="tw-mx-auto tw-mb-4 tw-mt-8" />
    </template>

    <template #content>
      <slot name="content"> </slot>
    </template>

    <template #footer>
      <div class="tw-flex tw-flex-wrap tw-justify-center tw-gap-4">
        <ModalFooterBtn
          btn-type="cancel"
          size="sm"
          label="Cancel"
          :disabled="loading"
          v-playwright="'confirm-modal-cancel-btn'"
          @click="emit('close', false)"
        />
        <ModalFooterBtn
          ref="confirmButton"
          size="sm"
          :btn-type="confirmType"
          :label="confirmText"
          :disabled="loading"
          :icon="confirmIcon"
          v-playwright="'confirm-modal-confirm-btn'"
          @click="onConfirm"
        />
      </div>
    </template>
  </GpModal>
</template>

<style scoped></style>
